import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { usePressAction } from '../hooks/usePressAction'; // [FLUID-8 2026-10-12]
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { transition, fluid } from '../styles/motion';
import { usePresence } from '../hooks/usePresence'; // [FLUID-1 2026-10-12]
import { fluidPop, fluidCascade } from './Fluid'; // [FLUID-1 2026-10-12]
// [LIQUID-GLASS 2026-11-02, user] the Payments toolbar button + its list use the shared liquid glass recipe
import {
  lgPopover, lgControlBg, lgControlHover, lgCardSheen, lgRim, lgPress, lgSunken, lgHair, lgGreen,
  lgButtonPrimary, lgButtonSecondary, LG_PRESS, LgPressProps,
} from '../styles/liquidGlass';
import { fetchPaymentColumns, PaymentTableColumn } from '../services/api';
import { pageRect, pageViewport } from '../utils/appZoom'; // [APP-FIT 2026-10-26] the menu is placed in page px

/*
 * [EDIT-COLUMNS 2026-09-28, user] "Edit Columns" — the admin-only picker on the Payments toolbar.
 *
 * Lists every real column of the `payments` table (read live from the database by
 * GET /api/payments/columns, so a column added later shows up by itself), lets the admin tick
 * any of them, and hands the ticked set to the page on Apply. Nothing changes until Apply:
 * ticking only edits a draft; Cancel, Escape or a click outside throws the draft away.
 *
 * The trigger copies GlassSelect's trigger so it sits in the toolbar as one of the dropdowns.
 * The popover is portalled to <body> with fixed positioning so the toolbar's own overflow can
 * never clip it, is capped to the space on screen (opening upward when there is more room
 * above), and always keeps its search box and Apply/Cancel row in view.
 */

const POP_WIDTH = 320;
const GAP = 6;
const MARGIN = 8;

/* [LIQUID-GLASS 2026-11-02, user] A glass capsule like the Payments filter buttons beside it (GlassSelect liquid): light
   top edge, squish + press light, a soft green glow around it while its list is open. */
const Trigger = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $open: boolean }>`
  appearance: none;
  -webkit-appearance: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.theme.text};
  cursor: pointer;
  height: 34px;
  padding: 0 36px 0 15px;
  border: none;
  border-radius: 999px;
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  background-color: ${lgControlBg};
  background-image: ${lgCardSheen};
  /* [STALE-PRESS 2026-11-02, user] no live blur of its own, like the filter buttons (components/GlassSelect.tsx) */
  ${lgRim}
  ${lgPress}
  box-shadow: ${(p) => (p.$open
    ? `0 0 0 1px color-mix(in srgb, ${lgGreen(p)} 70%, transparent), 0 0 0 4px color-mix(in srgb, ${lgGreen(p)} 16%, transparent), 0 0 18px color-mix(in srgb, ${lgGreen(p)} 24%, transparent)`
    : (p.theme.colorScheme === 'dark' ? '0 1px 1px rgba(0,0,0,0.25), 0 10px 24px -12px rgba(0,0,0,0.6)' : '0 1px 2px rgba(22,25,23,0.06), 0 8px 20px -10px rgba(22,25,23,0.24)'))};

  &:hover {
    background-color: ${lgControlHover};
  }

  /* phones: 36 px tall, like the filter buttons */
  @media (max-width: 768px) {
    height: 36px;
    padding: 0 30px 0 12px;
  }
`;

const Count = styled.span`
  position: relative;
  z-index: 1;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(180deg, rgba(255,255,255,0.35), rgba(255,255,255,0) 60%), ${(p) => p.theme.green600};
`;

const Chevron = styled.span<{ $open: boolean }>`
  position: absolute;
  right: 13px;
  top: 50%;
  margin-top: -6px;
  width: 12px;
  height: 12px;
  display: inline-flex;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#d4d4d4' : '#505553')};
  transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')});
  transition: ${transition.hover};
  pointer-events: none;
`;

const Popover = styled.div<{ $top: number; $left: number; $maxH: number; $open: boolean; $up: boolean }>`
  position: fixed;
  top: ${(p) => p.$top}px;
  left: ${(p) => p.$left}px;
  width: ${POP_WIDTH}px;
  max-width: calc(100vw / var(--app-zoom, 1) - ${MARGIN * 2}px); /* [APP-FIT 2026-10-26] the window in page px */
  max-height: ${(p) => p.$maxH}px;
  z-index: 1300;
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  overflow: hidden;
  color: ${(p) => p.theme.text};
  /* [LIQUID-GLASS 2026-11-02, user] The glass panel; only the column list inside it scrolls. [STALE-PRESS 2026-11-02, user]
     Without the live blur (lgPopover): it opens next to buttons, like the filter lists (styles/liquidGlass.ts says why). */
  ${lgPopover}
  /* [FLUID-1 2026-10-12, user] Opens like GlassSelect's list: grows out of the trigger's corner on the
     spring (from the bottom corner when it opens upward), its column rows rising in one after another
     (components/Fluid.tsx). It used to appear in one frame. */
  ${(p) => fluidPop({ open: p.$open, origin: `0 ${p.$up ? '100%' : '0'}`, from: `translateY(${p.$up ? 8 : -8}px) scale(0.92)` })}
  ${(p) => (p.$open ? fluidCascade('label') : '')}
`;

const Head = styled.div`
  flex: none;
  padding: 10px 10px 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-bottom: 1px solid ${lgHair};
`;

const Title = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;

const Search = styled.input`
  font-family: inherit;
  font-size: 13px;
  padding: 7px 12px;
  border-radius: 999px;
  color: ${(p) => p.theme.text};
  outline: none;
  ${lgSunken} /* [LIQUID-GLASS 2026-11-02] pressed into the glass; green ring + glow while typing */
  &::placeholder { color: ${(p) => p.theme.dim}; }
`;

const List = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 6px;
`;

const Row = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 13px;
  transition: background-color 150ms ease;
  &:hover { background: ${lgControlHover}; } /* [LIQUID-GLASS 2026-11-02] a soft glass highlight */
  input { accent-color: ${(p) => p.theme.green600}; width: 15px; height: 15px; flex: none; cursor: pointer; }
`;

const ColName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
`;

const TypeTag = styled.span`
  flex: none;
  font-size: 10.5px;
  padding: 1px 6px;
  border-radius: 6px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : p.theme.pill)};
`;

const Note = styled.div`
  padding: 14px 10px;
  font-size: 13px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const Foot = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-top: 1px solid ${lgHair};
`;

// [BUTTON-STYLE 2026-09-30] $primary (Apply) = the green primary, otherwise the secondary.
// [LIQUID-GLASS 2026-11-02] Glass capsules (squish + press light).
const Btn = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $primary?: boolean }>`
  ${(p) => (p.$primary ? lgButtonPrimary : lgButtonSecondary)}
`;

interface Props {
  /** The columns currently added to the table (what Apply last saved). */
  selected: string[];
  /** Called with the new selection on Apply, in the table's column order. */
  onApply: (next: string[]) => void;
}

interface Placement { top: number; left: number; maxH: number; up: boolean }

const EditColumnsMenu: React.FC<Props> = ({ selected, onApply }) => {
  const [open, setOpen] = useState(false);
  const [cols, setCols] = useState<PaymentTableColumn[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState<Placement>({ top: 0, left: 0, maxH: 420, up: false });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  // [FLUID-1 2026-10-12] Keeps the popover mounted through its 200 ms close so the exit can play;
  // `open` stays the source of truth for behaviour (focus, outside click, Escape).
  const pres = usePresence(open, fluid.outMs);

  const load = useCallback(() => {
    setLoadError(false);
    fetchPaymentColumns().then(setCols).catch(() => setLoadError(true));
  }, []);

  // Below the trigger when there is room; above it when there is clearly more room there (phones,
  // a trigger low on the screen). The height is capped to the space available either way.
  const place = useCallback(() => {
    // [APP-FIT 2026-10-26] In page px (utils/appZoom), like the top / left / max-height it sets.
    const r = triggerRef.current ? pageRect(triggerRef.current) : null;
    if (!r) return;
    const { width: vw, height: vh } = pageViewport();
    const width = Math.min(POP_WIDTH, vw - MARGIN * 2);
    const below = vh - r.bottom - GAP - MARGIN;
    const above = r.top - GAP - MARGIN;
    const up = below < 260 && above > below;
    const maxH = Math.max(180, Math.min(460, up ? above : below));
    const top = up ? Math.max(MARGIN, r.top - GAP - maxH) : r.bottom + GAP;
    const left = Math.max(MARGIN, Math.min(r.left, vw - width - MARGIN));
    setPos((p) => (p.top === top && p.left === left && p.maxH === maxH && p.up === up ? p : { top, left, maxH, up }));
  }, []);

  const openMenu = () => {
    setDraft(new Set(selected));
    setQuery('');
    place();
    setOpen(true);
    if (!cols) load();
  };
  /** Close; `refocus` returns keyboard focus to the trigger (not after a click elsewhere). */
  const close = useCallback((refocus = false) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);
  // [FLUID-8 2026-10-12, user] Opens on the mouse PRESS, like the dropdowns (hooks/usePressAction).
  const openPress = usePressAction<HTMLButtonElement>(() => (open ? close(false) : openMenu()));

  // Focus the search on open — but not on touch screens, where it would pop the keyboard up over the list.
  // [FLUID-1 2026-10-12] Waits for pres.mounted too: the popover now mounts one render after `open`.
  // [FLUID-8 2026-10-12] ONE frame later: the menu now opens on the mouse PRESS, and that press's own mousedown focuses
  // the Edit Columns button AFTER this effect — focused here, the search box lost focus to it at once (typing went
  // nowhere, and Space re-clicked the button). The same as GlassSelect focusing its list.
  useLayoutEffect(() => {
    if (!open || !pres.mounted) return undefined;
    const touch = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
    if (touch) return undefined;
    const id = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open, pres.mounted]);

  // Follow the trigger while open; close on Escape or a click outside (the draft is discarded).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      close(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(true); };
    // Scrolling the column list itself does not move the trigger; only page/ancestor scrolls do.
    const onScroll = (e: Event) => { if (popRef.current?.contains(e.target as Node)) return; place(); };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, close, place]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    // [PAYMENT-MODE 2026-10-18] A labelled entry (Subscription / Manual) is found by its label too.
    return (cols || []).filter((c) => !q || c.name.toLowerCase().includes(q) || (c.label || '').toLowerCase().includes(q));
  }, [cols, query]);

  const toggle = (name: string) => setDraft((prev) => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  // Without the column list (it failed to load) only REMOVING columns is possible, so an admin can
  // always take columns off the table.
  const addsUnknown = !cols && Array.from(draft).some((n) => !selected.includes(n));
  const apply = () => {
    // Keep the table's column order (the database order), and drop any name no longer in the table.
    const next = cols ? cols.map((c) => c.name).filter((n) => draft.has(n)) : selected.filter((n) => draft.has(n));
    onApply(next);
    close(true);
  };

  return (
    <>
      <Trigger
        ref={triggerRef}
        type="button"
        $open={open}
        aria-haspopup="dialog"
        aria-expanded={open}
        {...openPress} /* [FLUID-8 2026-10-12] on the press */
      >
        Edit Columns
        {selected.length > 0 && <Count aria-label={`${selected.length} added`}>{selected.length}</Count>}
        <Chevron $open={open} aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 4.5 6 7.5 9 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </Chevron>
      </Trigger>
      {pres.mounted && ReactDOM.createPortal(
        <Popover ref={popRef} $top={pos.top} $left={pos.left} $maxH={pos.maxH} $open={open && pres.entered} $up={pos.up} role="dialog" aria-label="Edit columns">
          <Head>
            <Title>
              <span>Payment table columns</span>
              <span>{draft.size} selected</span>
            </Title>
            <Search
              ref={searchRef}
              id="edit-columns-search"
              type="search"
              aria-label="Search columns"
              placeholder="Search columns"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </Head>
          <List>
            {loadError ? (
              <Note>
                Could not load the column list.
                <Btn type="button" onClick={load}>Retry</Btn>
              </Note>
            ) : !cols ? (
              <Note>Loading columns…</Note>
            ) : shown.length === 0 ? (
              <Note>No column matches “{query}”.</Note>
            ) : (
              shown.map((c) => (
                <Row key={c.name}>
                  <input type="checkbox" checked={draft.has(c.name)} onChange={() => toggle(c.name)} />
                  <ColName title={c.label ? `${c.label} (${c.name})` : c.name}>{c.label || c.name}</ColName>
                  <TypeTag>{c.type}</TypeTag>
                </Row>
              ))
            )}
          </List>
          <Foot>
            <Btn type="button" onClick={() => setDraft(new Set())} disabled={draft.size === 0}>Clear</Btn>
            <span style={{ flex: 1 }} />
            <Btn type="button" onClick={() => close(true)}>Cancel</Btn>
            <Btn type="button" $primary onClick={apply} disabled={addsUnknown}>Apply</Btn>
          </Foot>
        </Popover>,
        document.body,
      )}
    </>
  );
};

export default EditColumnsMenu;
